import { observe, makeAutoObservable } from 'mobx';
import {TaskTemplate} from 'main/stores/tasks';
import TaskTemplateForm from '../store/TaskTemplateForm';
import {endpoints, types} from 'main/tools';
import {DomainStore, fetchModels} from 'shared/store';
import Employee from 'main/stores/employees/Employee';
import {User} from 'main/stores/users';
import _ from 'lodash';

class TaskTemplateSelectorState {
  store = new DomainStore();
  config;
  hasManager;
  onChange;
  employeeId;
  localization;

  taskTemplate;
  taskTemplates = [];
  selectedTaskTemplates = [];
  defaultTaskTemplates = [];
  allEmployees = [];
  allUsers = [];

  addTaskTemplateModalOpen = false;
  editTaskTemplateModalOpen = false;
  errors = {};

  constructor() {
    makeAutoObservable(this, {
      store: false
    });
  }

  receiveProps({config, v, onChange, employeeId, hasManager = true}) {
    this.config = config;
    this.onChange = onChange;
    this.employeeId = employeeId;
    this.hasManager = hasManager;
    this.localization = {
      addModel: `tasks.${this.config.selectTasksTitle}`,
      emptyState: `tasks.${this.config.emptyState}`
    };
  }

  async load() {
    await this.store._compose([
      endpoints.EMPLOYEES.ALL,
      endpoints.USERS.ALL,
      endpoints.TASK_TEMPLATES.with(this.config.templateType, this.employeeId)
    ]);
    this.allEmployees = this.store.getAllOrdered(types.EMPLOYEE, Employee);
    this.allUsers = this.store._getAll(types.USER, User);
    this.taskTemplates = this.config.fetchTasks ? this.store._getAll(types.TASK_TEMPLATE).map(t => new TaskTemplate(t)) : [];
    this.defaultTaskTemplates = (await fetchModels(
      endpoints.TASK_TEMPLATES.DEFAULT_FOR_EMPLOYEE.with(this.employeeId, this.config.templateType),
      types.TASK_TEMPLATE
    )).map(t => new TaskTemplate({...t, default: true}));

    observe(this, 'taskTemplatesWithDefaults', () => this.emitOnChange(), true);
  }

  openAddTaskTemplateModal() {
    this.addTaskTemplateModalOpen = true;
  }

  closeAddTaskTemplateModal() {
    this.addTaskTemplateModalOpen = false;
    this.selectedTaskTemplates = [];
  }

  saveSelectedTaskTemplates() {
    this.selectedTaskTemplates.forEach(taskTemplate => {
      this.taskTemplates.push(taskTemplate);
    });
    this.closeAddTaskTemplateModal();
  }

  openEditTaskTemplateModal(taskTemplate) {
    this.taskTemplate = new TaskTemplate(taskTemplate);
    this.openEditModal();
  }

  openCreateTaskTemplateModal() {
    this.taskTemplate = new TaskTemplate({templateType: this.config.templateType});
    this.openEditModal();
  }

  openEditModal() {
    this.taskTemplateForm = new TaskTemplateForm(this.taskTemplate, this.allEmployees, this.allUsers, this.hasManager);
    this.editTaskTemplateModalOpen = true;
  }

  closeEditTaskTemplateModal() {
    this.editTaskTemplateModalOpen = false;
    this.errors = {};
  }

  saveTaskTemplate() {
    if (!this.validateTaskTemplate()) return null;

    const existingTaskTemplate = _.find(this.taskTemplatesWithDefaults,
      { clientId: this.taskTemplate.clientId }
    );

    if (existingTaskTemplate) {
      existingTaskTemplate.merge(this.taskTemplate);
      if (existingTaskTemplate.default) {
        existingTaskTemplate.default = false;
        _.remove(this.defaultTaskTemplates, { clientId: existingTaskTemplate.clientId });
        this.taskTemplates.unshift(existingTaskTemplate);
      }
    } else {
      this.taskTemplates.push(this.taskTemplate);
    }

    this.closeEditTaskTemplateModal();
  }

  validateTaskTemplate() {
    this.errors = this.taskTemplateForm.validate();
    return _.isEmpty(this.errors);
  }

  removeTaskTemplate(taskTemplate) {
    if (taskTemplate.default) {
      _.remove(this.defaultTaskTemplates, { clientId: taskTemplate.clientId });
    } else {
      _.remove(this.taskTemplates, { clientId: taskTemplate.clientId });
    }
  }

  toggleTaskTemplateSelected(taskTemplate) {
    if (this.taskTemplateSelected(taskTemplate)) {
      _.remove(this.selectedTaskTemplates, { clientId: taskTemplate.clientId });
    } else {
      this.selectedTaskTemplates.push(taskTemplate);
    }
  }

  taskTemplateSelected(taskTemplate) {
    return _.some(this.selectedTaskTemplates, { clientId: taskTemplate.clientId });
  }

  emitOnChange() {
    if (this.onChange) {
      this.onChange(this.taskTemplatesWithDefaults);
    }
  }

  get taskTemplatesWithDefaults() {
    return this.defaultTaskTemplates.concat(this.taskTemplates);
  }
}

export default TaskTemplateSelectorState;
